import {useEffect, useRef} from 'react'
import {getOmsPlatformFieldDataOptionsAsync} from 'src/redux/actions/omsPageActions'
import {useAppDispatch} from 'src/redux/storeHooks'
import {PlatformFieldDataSelectOption} from './types'

/** Query param for GET /api/platform-fields/{platformId} when loading order config fields */
export const OMS_ORDER_PLATFORM_FIELD_OWNER = 'ORDER'

const OPEN_STATUS_TOKEN = 'OPEN'
const DRAFT_STATUS_TOKEN = 'DRAFT'
const MANUAL_CHANNEL_TOKENS = ['MANUAL_ORDER', 'MANUAL'] as const

const normalizeFieldToken = (value: string | null | undefined): string =>
  (value || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_')

const isAbortedRequest = (error: any): boolean =>
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED'

export const isEmptyOrderFieldValue = (value: string | null | undefined): boolean =>
  value == null || String(value).trim() === ''

export const extractPlatformFieldsList = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.results)) return payload.results
  const data = payload?.data ?? payload
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

type FindPlatformFieldOptions = {
  /** Prefer a field with this input_type when multiple share the same api_code */
  inputType?: string
}

/** Match a native ORDER field by api_code / api_name (e.g. CHANNEL, PAYMENT_METHOD, SHIPPINGPROVIDER) */
export const findPlatformFieldByCode = (
  fields: any[],
  fieldCode: string,
  options?: FindPlatformFieldOptions
) => {
  if (!fieldCode) return null

  const matches = fields.filter((f) => f.api_code === fieldCode || f.api_name === fieldCode)
  if (!matches.length) return null

  if (options?.inputType) {
    const typed = matches.find((f) => f.input_type === options.inputType)
    if (typed) return typed
  }

  return matches[0]
}

export const extractPlatformFieldDataResults = (payload: unknown): any[] => {
  if (Array.isArray(payload)) return payload

  const root = payload as any
  if (Array.isArray(root?.results)) return root.results
  if (Array.isArray(root?.data)) return root.data
  if (Array.isArray(root?.results?.data)) return root.results.data
  if (Array.isArray(root?.data?.results)) return root.data.results
  if (Array.isArray(root?.data?.data)) return root.data.data
  if (Array.isArray(root?.data?.data?.results)) return root.data.data.results
  return []
}

export const toPlatformFieldDataOption = (item: any): PlatformFieldDataSelectOption | null => {
  if (!item || item.id == null) return null
  return {
    value: String(item.id),
    label: item.api_name ?? item.api_id ?? String(item.id),
    raw: item,
  }
}

export const pickFirstPlatformFieldOption = (
  options: PlatformFieldDataSelectOption[]
): PlatformFieldDataSelectOption | null => options.find((option) => option.value) ?? null

const optionMatchesStatusToken = (
  option: PlatformFieldDataSelectOption,
  statusToken: string
): boolean => {
  const normalizedTarget = normalizeFieldToken(statusToken)
  const tokens = [option.raw?.api_id, option.raw?.api_name, option.raw?.api_code, option.label]
  return tokens.some((token) => normalizeFieldToken(token) === normalizedTarget)
}

export const pickPlatformFieldOptionByStatusToken = (
  options: PlatformFieldDataSelectOption[],
  statusToken: string
): PlatformFieldDataSelectOption | null =>
  options.find((option) => optionMatchesStatusToken(option, statusToken)) ?? null

export const pickOpenOrderStatusOption = (
  options: PlatformFieldDataSelectOption[]
): PlatformFieldDataSelectOption | null =>
  pickPlatformFieldOptionByStatusToken(options, OPEN_STATUS_TOKEN)

export const pickDraftOrderStatusOption = (
  options: PlatformFieldDataSelectOption[]
): PlatformFieldDataSelectOption | null =>
  pickPlatformFieldOptionByStatusToken(options, DRAFT_STATUS_TOKEN)

export const pickManualOrderChannelOption = (
  options: PlatformFieldDataSelectOption[]
): PlatformFieldDataSelectOption | null => {
  for (const token of MANUAL_CHANNEL_TOKENS) {
    const match = pickPlatformFieldOptionByStatusToken(options, token)
    if (match) return match
  }

  return (
    options.find((option) => {
      const label = normalizeFieldToken(option.label)
      return label.includes('MANUAL') && label.includes('ORDER')
    }) ?? null
  )
}

type UseDefaultOmsPlatformFieldOptionArgs = {
  fieldId: number | null
  enabled: boolean
  pick: (options: PlatformFieldDataSelectOption[]) => PlatformFieldDataSelectOption | null
  onApply: (option: PlatformFieldDataSelectOption) => void
}

export const useDefaultOmsPlatformFieldOption = ({
  fieldId,
  enabled,
  pick,
  onApply,
}: UseDefaultOmsPlatformFieldOptionArgs) => {
  const dispatch = useAppDispatch()
  const pickRef = useRef(pick)
  const onApplyRef = useRef(onApply)

  pickRef.current = pick
  onApplyRef.current = onApply

  useEffect(() => {
    if (!enabled || fieldId == null) return

    const abort = new AbortController()
    let cancelled = false

    const load = async () => {
      try {
        const payload = await dispatch(
          getOmsPlatformFieldDataOptionsAsync({
            fieldId,
            search_string: '',
            signal: abort.signal,
          })
        ).unwrap()

        if (cancelled) return

        const options = extractPlatformFieldDataResults(payload)
          .map(toPlatformFieldDataOption)
          .filter((option): option is PlatformFieldDataSelectOption => option != null)
        const picked = pickRef.current(options)
        if (picked) onApplyRef.current(picked)
      } catch (error) {
        if (isAbortedRequest(error)) return
      }
    }

    void load()

    return () => {
      cancelled = true
      abort.abort()
    }
  }, [dispatch, enabled, fieldId])
}
